import { Node, mergeAttributes, nodePasteRule } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';

import { MentionNodeView } from './mention-node-view';
import { suggestionOptions } from './suggestion-options';

/**
 * Regular expression to detect placeholders with optional default values.
 *
 * Supported syntax:
 *   - {{ variable }}
 *   - {{ variable | default('value') }}
 *   - {{variable}}
 *   - {{variable|default('value')}}
 *
 * Capturing groups:
 *   1. Variable name (string)
 *   2. Default value (string, optional)
 */
const MENTION_REGEX = /\{\{\s*([a-zA-Z0-9_]+)(?:\s*\|\s*default\('([^']+)'\))?\s*\}\}/g;

interface MentionOptions {
  HTMLAttributes: Record<string, any>;
  items: string[];
}

export interface Mention {
  mention: string;
  default?: string | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    mention: { insertMention: (options: Mention) => ReturnType };
  }
}

export const Mention = Node.create<MentionOptions>({
  name: 'mention',
  atom: true,
  group: 'inline',
  inline: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      items: [],
    };
  },

  addAttributes() {
    return {
      mention: { default: null },
      default: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-mention]' }];
  },

  /**
   * Helps to render the mention node as pure HTML.
   * Used when `editor.getHTML()` is called.
   */
  renderHTML({ node, HTMLAttributes }) {
    const defaultValue = node.attrs.default || '';
    const mentionValue = node.attrs.mention || '';

    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-mention': mentionValue,
        'data-default': defaultValue,
        class: 'mention',
      }),
      defaultValue ? `{{ ${mentionValue} | default('${defaultValue}') }}` : `{{ ${mentionValue} }}`,
    ];
  },

  /**
   * Render the mention node as plain text with optional default value. Helps to export/copy the content.
   */
  renderText({ node }) {
    const defaultValue = node.attrs.default || '';
    const mentionValue = node.attrs.mention || '';

    return defaultValue ? `{{${mentionValue}|default('${defaultValue}')}}` : `{{${mentionValue}}}`;
  },

  /**
   * Detects specific patterns in pasted text and converts them into custom nodes.
   */
  addPasteRules() {
    return [
      nodePasteRule({
        find: MENTION_REGEX,
        type: this.type,
        getAttributes: (match) => ({
          mention: match[1],
          default: match[2] || null,
        }),
      }),
    ];
  },

  /**
   * Defines a command to insert a mention node with the given attributes.
   */
  addCommands() {
    return {
      insertMention:
        (attrs) =>
        ({ chain }) => {
          return chain()
            .insertContent([{ type: this.name, attrs }])
            .run();
        },
    };
  },

  /**
   * Defines a custom React node view for mentions, rendering them as clickable span that allow updating the default value.
   */
  addNodeView() {
    return ReactNodeViewRenderer(MentionNodeView);
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...suggestionOptions({ items: this.options.items }),
      }),
    ];
  },
});
